import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Pill, Trash2, FileText, Info, AlertCircle, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { prescriptionService } from '@/services/prescriptionService';
import { useToast } from '@/hooks/use-toast';
import { Prescription } from '@/lib/types';

interface EditMedicationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prescription: Prescription;
  onMedicationsUpdated: () => void;
}

const EditMedicationsDialog: React.FC<EditMedicationsDialogProps> = ({
  open,
  onOpenChange,
  prescription,
  onMedicationsUpdated,
}) => {
  const { toast } = useToast();
  const [isRemoving, setIsRemoving] = useState(false);
  const [medicationToRemove, setMedicationToRemove] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleRemoveClick = (medication: any) => {
    setMedicationToRemove(medication);
    setShowConfirmDialog(true);
  };

  const handleConfirmRemove = async () => {
    if (!medicationToRemove) return;

    setIsRemoving(true);
    try {
      const result = await prescriptionService.deleteMedicationRequest(
        prescription.id,
        medicationToRemove.id
      );

      if (result.success) {
        toast({
          title: 'Medication Removed',
          description: `${medicationToRemove.medication} has been removed from the prescription.`,
        });
        onMedicationsUpdated();
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to remove medication',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error removing medication:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove medication. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsRemoving(false);
      setShowConfirmDialog(false);
      setMedicationToRemove(null);
    }
  };

  const medications = prescription?.medicationRequests || [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='max-w-3xl max-h-[85vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-xl font-semibold text-slate-900'>
              Edit Medications
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Review and remove medications from this prescription. Click "Remove" to delete a
              medication.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-4'>
            {medications.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-12 text-center'>
                <AlertCircle className='h-12 w-12 text-slate-300 mb-3' />
                <p className='text-sm text-slate-500'>No medications in this prescription</p>
              </div>
            ) : (
              <>
                <div className='space-y-3'>
                  {medications.map((med, index) => {
                    const medicationName = med.medication || '--';
                    const qty = Number(med.quantity_authorized) || 1;
                    const priceCents =
                      med.selling_price_cents != null
                        ? med.selling_price_cents
                        : med?.clinicProduct?.selling_price_cents != null
                          ? med.clinicProduct.selling_price_cents * qty
                          : null;
                    const price = priceCents != null ? (priceCents / 100).toFixed(2) : null;

                    return (
                      <motion.div
                        key={med.id || index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.2 }}
                      >
                        <Card className='overflow-hidden border-slate-200 shadow-sm hover:shadow transition-shadow'>
                          <CardContent className='p-4'>
                            <div className='flex items-start gap-3'>
                              <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10'>
                                <Pill className='h-5 w-5 text-primary' />
                              </div>
                              <div className='min-w-0 flex-1'>
                                <div className='flex items-start justify-between gap-2'>
                                  <div className='flex items-center gap-2 flex-wrap'>
                                    <h5 className='text-sm font-semibold text-slate-900'>
                                      {medicationName}
                                    </h5>
                                    {qty > 1 && (
                                      <Badge variant='secondary' className='text-xs font-medium text-slate-600 bg-slate-100'>
                                        Qty × {qty}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className='flex items-center gap-2'>
                                    {price && (
                                      <div className='flex items-baseline gap-1'>
                                        <Badge className='border-0 bg-green-100 font-semibold text-green-700'>
                                          ${price}
                                        </Badge>
                                        {qty > 1 && (
                                          <span className='text-xs text-slate-500'>
                                            (${(Number(price) / qty).toFixed(2)} each)
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    <Button
                                      type='button'
                                      variant='ghost'
                                      size='sm'
                                      className='h-8 px-2 text-slate-500 hover:bg-red-50 hover:text-red-600'
                                      onClick={() => handleRemoveClick(med)}
                                      disabled={isRemoving || medications.length === 1}
                                      title={
                                        medications.length === 1
                                          ? 'Cannot remove the last medication'
                                          : 'Remove medication'
                                      }
                                    >
                                      <Trash2 className='mr-1 h-3.5 w-3.5' />
                                      Remove
                                    </Button>
                                  </div>
                                </div>

                                {/* Details Grid */}
                                <div className='mt-3 grid grid-cols-4 gap-3'>
                                  <div className='rounded-md bg-slate-50 p-2'>
                                    <span className='block text-xs text-slate-500'>Dose</span>
                                    <p
                                      className='truncate text-xs font-medium text-slate-900'
                                      title={med.dose}
                                    >
                                      {med.dose || '-'}
                                    </p>
                                  </div>
                                  <div className='rounded-md bg-slate-50 p-2'>
                                    <span className='block text-xs text-slate-500'>Quantity</span>
                                    <p className='text-xs font-medium text-slate-900'>
                                      {med.quantity_authorized || '-'}
                                    </p>
                                  </div>
                                  <div className='rounded-md bg-slate-50 p-2'>
                                    <span className='block text-xs text-slate-500'>
                                      Days Supply
                                    </span>
                                    <p className='text-xs font-medium text-slate-900'>
                                      {med.day_supply || '-'}
                                    </p>
                                  </div>
                                  <div className='rounded-md bg-slate-50 p-2'>
                                    <span className='block text-xs text-slate-500'>Refills</span>
                                    <p className='text-xs font-medium text-slate-900'>
                                      {med.refills_authorized || '0'}
                                    </p>
                                  </div>
                                </div>

                                {/* Instructions */}
                                {med.sig && (
                                  <div className='mt-3 rounded-md border border-blue-100 bg-blue-50 p-2.5'>
                                    <div className='flex items-start gap-2'>
                                      <FileText className='mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-blue-500' />
                                      <p className='text-xs leading-relaxed text-blue-800'>
                                        {med.sig}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Additional Details */}
                                {(med.flavor || med.notes) && (
                                  <div className='mt-3 grid grid-cols-2 gap-2'>
                                    {med.flavor && (
                                      <div className='rounded-md bg-slate-50 p-2'>
                                        <span className='block text-xs text-slate-500'>Flavor</span>
                                        <p className='text-xs font-medium text-slate-900'>
                                          {med.flavor}
                                        </p>
                                      </div>
                                    )}
                                    {med.notes && (
                                      <div className='rounded-md bg-slate-50 p-2'>
                                        <span className='block text-xs text-slate-500'>Notes</span>
                                        <p className='text-xs font-medium text-slate-900'>
                                          {med.notes}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>

                {medications.length === 1 && (
                  <div className='flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-3'>
                    <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600' />
                    <span className='text-sm text-amber-700'>
                      This is the last medication in the prescription. At least one medication is
                      required.
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={() => onOpenChange(false)}
              disabled={isRemoving}
              className='min-w-[100px]'
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Medication?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <strong>{medicationToRemove?.medication || 'this medication'}</strong> from this
              prescription? This action cannot be undone, but will be recorded in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              disabled={isRemoving}
              className='bg-red-600 hover:bg-red-700'
            >
              {isRemoving ? (
                <div className='flex items-center space-x-2'>
                  <RefreshCw className='h-4 w-4 animate-spin' />
                  <span>Removing...</span>
                </div>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditMedicationsDialog;
